/**
 * Detalle en vivo de una campaña del WhatsApp oficial (`campanas/:id`).
 *
 * - Se refresca sola según el estado (`intervaloDetalle`): cada 3 s mientras envía, cada 30 s
 *   programada o frenada, nada en el resto. El resultado, cada 10 s mientras envía.
 * - Un dato que no se pudo medir se dice, no se disfraza de 0 (`CampanaDetallePage.tsx`): si el
 *   resultado no llega, el embudo dice «No pudimos traer los resultados»; si el refresco se cae,
 *   se mantiene lo último visto con una franja «Dejamos de actualizar…».
 * - Los botones (pausar, reanudar, cancelar) salen de los flags del backend, nunca de una cuenta
 *   del front: el backend sabe si hay un despacho en curso. La única excepción apaga, no prende: una
 *   campaña confirmada en otro modo que el del servidor (`otroModo`, C1) no ofrece Reanudar aunque
 *   un backend anterior diga que se puede (nunca iba a salir con la cuenta de hoy). Su aviso dice de
 *   qué modo es: la de prueba del simulado (se cancela y se arma otra) o una real confirmada con
 *   Twilio y el servidor de vuelta en simulado (no se envía, y el aviso no ofrece cancelarla).
 * - «Ver a quiénes» filtra la bitácora SÓLO por código (junta `fallido` y `no_entregado`, igual que
 *   la tabla de fallidos).
 * - Las estadísticas de la campaña (`/estadisticas/campanas/:id`: tiempos, por grupo, botones y
 *   bajas, costo según Twilio, clics y la serie por hora) se piden aparte, al abrir «En vivo» o
 *   «Resultado» y cuando cambia el estado. Si fallan, lo de siempre se sigue viendo: sólo esas
 *   tarjetas dicen «No pudimos traer…».
 * - Una persona de un cliente (Accesos, rol «comercio») ve lo que se le cobra a ella (el costo, lo
 *   estimado y el desvío), no lo que paga Deenex («Deenex pagó») ni el precio que informa Twilio, y
 *   no ve la Auditoría (los mails y las IP de quienes del equipo armaron y confirmaron la campaña):
 *   quien la confirmó es «Equipo Deenex». El backend tampoco le manda esas claves: la pantalla no
 *   cuenta con ellas (sin «NaN» ni «USD undefined»).
 * - El equipo duplica la campaña en cualquier estado (abre el asistente en el borrador nuevo) y borra un borrador, con la
 *   confirmación en la misma pantalla (`AccionesCampana`); borrado, vuelve a la lista con el aviso.
 * - «Hacer un seguimiento» (28/10/2026): el equipo, en una campaña que ya mandó y no tiene el suyo (`puedeHacerSeguimiento`
 *   del backend), va a la pantalla para elegir el criterio. El detalle de la original dice cuál es su seguimiento, y el de
 *   la hija de cuál es (`RenglonSeguimiento`, también para el cliente). Un seguimiento no se duplica.
 * - «Repetir esta campaña» (28/10/2026): el equipo, en una campaña confirmada que no es un seguimiento ni una repetición
 *   (`puedeRepetir`), va a la página para elegir cada cuánto. La madre muestra «Se repite cada… · próxima: …» con
 *   «Pausar», «Terminar» y sus repeticiones (`TarjetaRepeticion`); la hija, «Repetición N de <madre>» y, si quedó en
 *   borrador, por qué (`RenglonRepeticion`). La regla y las repeticiones las ve también el cliente; los botones, no.
 */

import { useCallback, useEffect, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity, AlertTriangle, ArrowLeft, BarChart3, CheckCheck, CheckCircle2, Clock, History, Inbox, Loader2,
  CornerDownRight, MessageSquare, OctagonAlert, Pause, Play, RefreshCw, Repeat, Send, UserMinus, X, XCircle,
} from "lucide-react";
import type { CampanaDetalle, CodigoCorte, EsperaCampana, Medicion, RespuestaEntrante, Resultado, AudienciaActiva } from "../types";
import { ORDEN_GATES } from "../types";
import { oficialApi } from "../api";
import { useCuentaOficial } from "../contexto";
import { usePolling } from "../hooks/usePolling";
import { intervaloDetalle } from "../logic/polling";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import {
  CORTE, ESPERA, MOTIVO_EXCLUSION, SUBTITULO_MEDICION, SUBTITULO_MEDICION_BASE, TITULO_MEDICION, TITULO_MEDICION_BASE, detalleEspera, motivoCorteVisible, textoCorte, textoModoDistinto, textoRechazoPlantilla,
} from "../logic/textos";
import { nombreDeQuienPara, usd } from "../logic/formato";
import { ChipEstadoCampanaOficial } from "../components/ChipEstadoCampanaOficial";
import { EmbudoOficial } from "../components/EmbudoOficial";
import { FallidosPorMotivo } from "../components/FallidosPorMotivo";
import { BitacoraMensajes } from "../components/BitacoraMensajes";
import { BajarResultadosCampana } from "../components/BajarResultadosCampana";
import { ListaRespuestas } from "../components/ListaRespuestas";
import { SimularRespuesta } from "../components/SimularRespuesta";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { MedicionRecompra } from "../components/MedicionRecompra";
import { MedicionBaseVentas } from "../components/MedicionBaseVentas";
import { PlanTandas } from "../components/PlanTandas";
import { textoHorario } from "../logic/horario";
import { ModalMotivo } from "../components/ModalMotivo";
import { BorrarBorrador, BotonDuplicarCampana } from "../components/AccionesCampana";
import { RenglonSeguimiento } from "../components/SeguimientoCampana";
import { rutaHacerSeguimiento } from "../logic/seguimiento";
import { RenglonRepeticion, TarjetaRepeticion } from "../components/RepeticionCampana";
import { rutaRepetir } from "../logic/repeticion";
import { Tarjeta } from "../components/Tarjeta";
import { estadisticasApi } from "../estadisticasApi";
import { BASE_TASA } from "../logic/estadisticas";
import type { EstadisticasCampana } from "../types.estadisticas";
import { TarjetaTiempos } from "../components/estadisticas/TarjetaTiempos";
import { TarjetaRespuestas } from "../components/estadisticas/TarjetaRespuestas";
import { TarjetaCostoTwilio } from "../components/estadisticas/TarjetaCostoTwilio";
import { TarjetaClics } from "../components/estadisticas/TarjetaClics";
import { TablaPorGrupo } from "../components/estadisticas/TablaPorGrupo";
import { GraficoPorHora } from "../components/estadisticas/GraficoPorHora";
import { ErrorTarjeta } from "../components/estadisticas/comun";
import { DetalleTecnico } from "../components/estadisticas/DetalleTecnico";
import StatTile from "../../campana-nueva/components/StatTile";
import { fechaHora, num, pct, plural } from "../../campana-nueva/utils/format";

const B = "/activa/oficial";
const RUTA_CUENTA = `${B}/configuracion?seccion=cuenta`;
const RUTA_SALDO = `${B}/configuracion?seccion=saldo`;
const NUMERO_PRUEBA_KEY = "deenex-activa:oficial:numero-prueba";

type TabKey = "vivo" | "fallidos" | "mensajes" | "respuestas" | "resultado" | "auditoria";
const TABS: { key: TabKey; label: string; icon: typeof Activity }[] = [
  { key: "vivo", label: "En vivo", icon: Activity },
  { key: "fallidos", label: "Fallidos", icon: XCircle },
  { key: "mensajes", label: "Mensajes", icon: MessageSquare },
  { key: "respuestas", label: "Respuestas", icon: Inbox },
  { key: "resultado", label: "Resultado", icon: BarChart3 },
  { key: "auditoria", label: "Auditoría", icon: History },
];
const esTab = (s: string | null): s is TabKey => !!s && TABS.some((t) => t.key === s);
/** La Auditoría dice quién del equipo hizo qué, con su mail y su IP: a una persona de un cliente no se le muestra. */
const TABS_CLIENTE = TABS.filter((t) => t.key !== "auditoria");

/** El aviso del asistente («Empezó el envío…», «Programada para…») habla de una campaña en marcha:
 *  con la campaña pausada, terminada o cancelada ya no es cierto y no se muestra. */
function avisoVigente(aviso: string, estado: CampanaDetalle["estado"]): boolean {
  return !!aviso && (estado === "programada" || estado === "enviando");
}

/** Estados en los que una campaña de otro modo todavía se puede cancelar (la terminada ya no pide nada). */
const ABIERTA: readonly CampanaDetalle["estado"][] = ["programada", "enviando", "pausada", "pausada_auto"];

const ACCION_AUDITORIA: Record<string, string> = {
  crear: "Creó", editar: "Editó", chequeo: "Calculó el chequeo", prueba: "Mandó una prueba", confirmar: "Confirmó el envío",
  pausar: "Pausó", reanudar: "Reanudó", cancelar: "Canceló", corte: "Se frenó por un problema",
};

/** Lo que se hizo con la repetición (28/10/2026), en la entrada «editar» de la madre. */
const ACCION_REPETICION: Record<string, string> = {
  programar: "Programó la repetición", cambiar: "Cambió la repetición", pausar: "Pausó la repetición", reanudar: "Reanudó la repetición", terminar: "Terminó la repetición",
  // una lista de exclusión que se borró y frenaba cada repetición (29/10/2026): `texto` es su nombre
  sacar_lista_borrada: "Sacó de la serie la lista de exclusión borrada",
};

/**
 * «Creó (copia de «Campeones»)» para la entrada con la que nace una campaña duplicada, «Creó (repetición 3 de
 * «Campeones»)» para la de una repetición, «Programó la repetición (Cada semana…)» para la regla de la madre, y la de la
 * original cuando se canceló su seguimiento sin que saliera nada (29/10/2026): queda libre para otro.
 */
function textoAccion(a: { accion: string; detalle?: unknown }): string {
  const base = ACCION_AUDITORIA[a.accion] || a.accion;
  const d = (a.detalle && typeof a.detalle === "object" ? a.detalle : {}) as {
    duplicadaDe?: unknown; nombreOrigen?: unknown; repeticionDe?: unknown; n?: unknown; repeticion?: unknown; texto?: unknown;
    seguimiento?: unknown; nombreSeguimiento?: unknown;
  };
  const origen = typeof d.nombreOrigen === "string" && d.nombreOrigen ? d.nombreOrigen : "otra campaña";
  if (a.accion === "crear" && d.repeticionDe) return `${base} (repetición ${typeof d.n === "number" ? d.n : "?"} de «${origen}»)`;
  if (a.accion === "editar" && typeof d.repeticion === "string" && ACCION_REPETICION[d.repeticion]) {
    return `${ACCION_REPETICION[d.repeticion]}${typeof d.texto === "string" && d.texto ? ` (${d.texto})` : ""}`;
  }
  if (a.accion === "editar" && d.seguimiento === "liberar") {
    const hija = typeof d.nombreSeguimiento === "string" && d.nombreSeguimiento ? `«${d.nombreSeguimiento}»` : "su seguimiento";
    return `Canceló ${hija} sin que saliera ningún mensaje: se puede armar otro seguimiento`;
  }
  return a.accion === "crear" && d.duplicadaDe ? `${base} (copia de «${origen}»)` : base;
}

/** Porcentaje de una tasa que llega como fracción (0.9517 → «95,2 %»). */
const tasaTexto = (t: number | null | undefined) => `${(Math.round((Number(t) || 0) * 1000) / 10).toLocaleString("es-AR")} %`;

type Aviso = { texto: string; link?: { label: string; ruta: string } };

/**
 * Pestañas con el contrato de `role="tablist"` (flechas, Home/End y roving tabindex), mismo
 * markup que `CampanaDetallePage.tsx`: declarar el rol sin cumplirlo promete un comportamiento
 * que un lector de pantalla no encuentra.
 */
function Tabs({ tabs, value, onChange, alerta }: { tabs: typeof TABS; value: TabKey; onChange: (t: TabKey) => void; alerta?: Partial<Record<TabKey, boolean>> }) {
  const onKeyDown = (e: KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.key === value);
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(tabs[next].key);
    document.getElementById(`tab-oficial-${tabs[next].key}`)?.focus();
  };
  return (
    <div role="tablist" aria-label="Secciones de la campaña" onKeyDown={onKeyDown}
      className="flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1">
      {tabs.map((t) => {
        const activo = value === t.key;
        const Icon = t.icon;
        return (
          <button key={t.key} id={`tab-oficial-${t.key}`} role="tab" aria-selected={activo} aria-controls={`panel-oficial-${t.key}`}
            tabIndex={activo ? 0 : -1} onClick={() => onChange(t.key)}
            className={`relative flex-shrink-0 h-9 px-3.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
              activo ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Icon size={14} className={activo ? "text-violet-600" : "text-slate-400"} />
            {t.label}
            {alerta?.[t.key] && (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                <span className="sr-only">(hay algo para revisar)</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Banner({ tono, icono, children }: { tono: "rojo" | "ambar" | "verde"; icono: ReactNode; children: ReactNode }) {
  const cls = tono === "rojo" ? "border-red-200 bg-red-50 text-red-800" : tono === "ambar" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800";
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${cls}`} role={tono === "verde" ? "status" : "alert"}>
      <span className="mt-0.5 flex-shrink-0">{icono}</span>
      <div className="min-w-0 flex-1 space-y-1">{children}</div>
    </div>
  );
}

export default function CampanaOficialDetallePage() {
  /**
   * Una persona de un cliente (Accesos, rol «comercio») ve la campaña y sus resultados; pausar,
   * reanudar, cancelar y seguir configurando son del equipo de Deenex (el backend se los niega).
   */
  const equipo = useEsEquipoDeenex();
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { modo, cuenta } = useCuentaOficial();

  const [campana, setCampana] = useState<CampanaDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [noEncontrada, setNoEncontrada] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [errorResultado, setErrorResultado] = useState("");
  const [cargandoResultado, setCargandoResultado] = useState(false);
  const [stats, setStats] = useState<EstadisticasCampana | null>(null);
  const [errorStats, setErrorStats] = useState("");
  const [respuestas, setRespuestas] = useState<RespuestaEntrante[] | null>(null);
  const [errorRespuestas, setErrorRespuestas] = useState("");
  const [cargandoRespuestas, setCargandoRespuestas] = useState(false);
  const [filtroMensajes, setFiltroMensajes] = useState<{ codigo?: string }>({});
  const [claveBitacora, setClaveBitacora] = useState(0);
  const [nombresGrupo, setNombresGrupo] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"" | "pausar" | "reanudar" | "cancelar">("");
  const [errorAccion, setErrorAccion] = useState<Aviso | null>(null);
  const [modalCancelar, setModalCancelar] = useState(false);
  // El aviso que deja el asistente al confirmar se muestra UNA vez: se lee y se saca del historial.
  // Y sólo mientras la campaña está programada o enviando (`avisoVigente`): la página se refresca
  // sola, y con la campaña ya terminada «Empezó el envío… sigue sola» contradecía al estado.
  const [aviso] = useState<string>(() => String((location.state as { aviso?: string } | null)?.aviso || ""));
  useEffect(() => {
    if ((location.state as { aviso?: string } | null)?.aviso) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pedida = sp.get("tab");
  // Un link a ?tab=auditoria que abre una persona de un cliente cae en «En vivo»: esa pestaña no es para ella.
  const tab: TabKey = esTab(pedida) && (equipo || pedida !== "auditoria") ? pedida : "vivo";
  const setTab = useCallback((t: TabKey) => {
    setSp((prev) => { const n = new URLSearchParams(prev); n.set("tab", t); return n; }, { replace: true });
  }, [setSp]);

  /** `lanzar`: en el refresco periódico, un fallo se lanza para que `usePolling` lo cuente. */
  const cargarCampana = useCallback(async (lanzar = false) => {
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.campana(id));
      if (!l.ok) throw Object.assign(new Error(l.error), { status: l.status, body: { error: l.error, code: l.code } });
      setCampana(l.data);
      setError("");
    } catch (e) {
      const x = leerError(e);
      if (x.status === 404) setNoEncontrada(true);
      else setError(textoError(x));
      if (lanzar) throw e;
    } finally {
      setCargando(false);
    }
  }, [id]);

  const cargarResultado = useCallback(async (lanzar = false) => {
    setCargandoResultado(true);
    try {
      const l = leerRespuesta<Resultado>(await oficialApi.resultado(id));
      if (!l.ok) throw Object.assign(new Error(l.error), { status: l.status, body: { error: l.error, code: l.code } });
      setResultado(l.data);
      setErrorResultado("");
    } catch (e) {
      // Se conserva el último resultado visto; si nunca hubo, el embudo lo dice en vez de pintar ceros.
      setErrorResultado(`No pudimos traer los resultados. ${textoError(leerError(e))}`);
      if (lanzar) throw e;
    } finally {
      setCargandoResultado(false);
    }
  }, [id]);

  const cargarStats = useCallback(async () => {
    try {
      const l = leerRespuesta<EstadisticasCampana>(await estadisticasApi.campana(id));
      if (!l.ok) { setErrorStats(textoError(l)); return; }
      if (!l.data) { setErrorStats("El servidor no devolvió las estadísticas."); return; }
      setStats(l.data);
      setErrorStats("");
    } catch (e) {
      // Se conservan las últimas vistas; si nunca hubo, las tarjetas lo dicen.
      setErrorStats(textoError(leerError(e)));
    }
  }, [id]);

  const cargarRespuestas = useCallback(async () => {
    setCargandoRespuestas(true);
    try {
      const l = leerRespuesta<RespuestaEntrante[]>(await oficialApi.respuestasCampana(id));
      if (!l.ok) { setErrorRespuestas(l.error || textoError(l)); return; }
      setRespuestas(Array.isArray(l.data) ? l.data : []);
      setErrorRespuestas("");
    } catch (e) {
      setErrorRespuestas(textoError(leerError(e)));
    } finally {
      setCargandoRespuestas(false);
    }
  }, [id]);

  useEffect(() => {
    setCargando(true);
    setNoEncontrada(false);
    setCampana(null);
    setResultado(null);
    setStats(null);
    setErrorStats("");
    setRespuestas(null);
    void cargarCampana();
  }, [cargarCampana]);

  // El resultado existe desde que empezó a salir; se vuelve a pedir cuando cambia el estado.
  const estado = campana?.estado;
  const empezo = !!campana?.iniciadaAt;
  useEffect(() => {
    if (empezo) void cargarResultado();
  }, [estado, empezo, cargarResultado]);
  // Las estadísticas, al empezar y en cada cambio de estado (sin polling: son caras de calcular).
  useEffect(() => {
    if (empezo) void cargarStats();
  }, [estado, empezo, cargarStats]);

  // Pestañas que dependen del resultado: se refresca al abrirlas.
  useEffect(() => {
    if ((tab === "fallidos" || tab === "resultado") && empezo) void cargarResultado();
    if ((tab === "vivo" || tab === "resultado") && empezo) void cargarStats();
    if (tab === "respuestas" && respuestas === null) void cargarRespuestas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Nombres de los grupos (la campaña sólo trae los ids). Si falla, se muestran los ids. Un público de
  // la base no tiene grupos: no se piden las audiencias de Activa.
  // sólo una audiencia de Activa tiene un análisis con nombres de grupo (la base y el Público no)
  const campanaActivaId = campana?.audiencia?.tipo === "base" || campana?.audiencia?.tipo === "publico" ? undefined : campana?.audiencia?.campanaActivaId;
  useEffect(() => {
    if (!campanaActivaId) return;
    let vivo = true;
    (async () => {
      try {
        const l = leerRespuesta<AudienciaActiva[]>(await oficialApi.audiencias());
        if (!vivo || !l.ok) return;
        const a = (l.data || []).find((x) => x.id === campanaActivaId);
        if (a) setNombresGrupo(Object.fromEntries(a.grupos.map((g) => [g.id, `${g.emoji} ${g.nombre}`.trim()])));
      } catch { /* sin nombres: quedan los ids */ }
    })();
    return () => { vivo = false; };
  }, [campanaActivaId]);

  const pollCampana = usePolling(() => cargarCampana(true), intervaloDetalle(estado), [id]);
  const pollResultado = usePolling(() => cargarResultado(true), estado === "enviando" ? 10000 : null, [id]);

  // ── Acciones ─────────────────────────────────────────────────────────────
  const accion = async (nombre: "pausar" | "reanudar" | "cancelar", motivo?: string) => {
    setBusy(nombre);
    setErrorAccion(null);
    try {
      const r = nombre === "pausar" ? await oficialApi.pausar(id) : nombre === "reanudar" ? await oficialApi.reanudar(id) : await oficialApi.cancelar(id, motivo || "");
      const l = leerRespuesta<CampanaDetalle>(r);
      if (!l.ok) {
        // Los tres rechazos por la plantilla llevan a ella (duplicarla es el arreglo de los dos nuevos)
        const dePlantilla = textoRechazoPlantilla(l.code);
        const link = dePlantilla && campana?.plantilla
          ? { label: "Ver plantilla", ruta: `${B}/plantillas/${encodeURIComponent(campana.plantilla.id)}` }
          : l.code === "MARCA_FRENADA" || l.code === "CUENTA_NO_VERIFICADA" ? { label: "Ir a Configuración", ruta: RUTA_CUENTA } : undefined;
        setErrorAccion({ texto: (l.code !== "PLANTILLA_NO_APROBADA" && dePlantilla) || l.error || textoError(l), link });
        return;
      }
      setCampana(l.data);
      setModalCancelar(false);
    } catch (e) {
      const x = leerError(e);
      setErrorAccion({ texto: x.code === "ESTADO_INVALIDO" ? `${x.error || "La campaña cambió de estado."} Recargá la página.` : textoError(x) });
    } finally {
      setBusy("");
    }
  };

  const verMensajesDe = (codigo: string) => {
    setFiltroMensajes({ codigo });
    setClaveBitacora((k) => k + 1);
    setTab("mensajes");
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (cargando && !campana) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
        <div className="h-64 rounded-2xl bg-slate-100 animate-pulse" />
      </div>
    );
  }
  if (noEncontrada) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center">
        <p className="text-sm text-slate-600">No encontramos esta campaña.</p>
        <Link to={`${B}/campanas`} className="mt-3 inline-block text-sm font-semibold text-violet-600 hover:underline">Volver a las campañas</Link>
      </div>
    );
  }
  if (!campana) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700" role="alert">
        <p className="font-semibold">No pudimos traer la campaña.</p>
        <p className="mt-1">{error}</p>
        <button type="button" onClick={() => { setCargando(true); void cargarCampana(); }} className="mt-3 inline-flex items-center gap-1.5 font-semibold underline">
          <RefreshCw size={13} /> Reintentar
        </button>
      </div>
    );
  }

  const c = campana;
  const rutaEditar = `${B}/campanas/${encodeURIComponent(c.id)}/editar`;
  const botonCancelar = equipo && c.puedeCancelar && (
    <button type="button" onClick={() => setModalCancelar(true)} disabled={!!busy}
      className="h-9 px-3 rounded-lg border border-red-200 bg-white text-xs font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50">
      <X size={13} /> Cancelar
    </button>
  );
  const modal = modalCancelar && (
    <ModalMotivo
      titulo="Cancelar la campaña"
      descripcion={
        c.seguimientoDe
          ? `Los mensajes que todavía no salieron no se mandan. Lo enviado no se puede deshacer. Si no salió ninguno, «${c.seguimientoDe.nombre}» queda libre para otro seguimiento; si salió alguno, no (nadie recibe dos seguimientos de la misma campaña).`
          : "Los mensajes que todavía no salieron no se mandan. Lo enviado no se puede deshacer."
      }
      confirmar="Cancelar la campaña"
      peligro
      busy={busy === "cancelar"}
      onConfirmar={(m) => void accion("cancelar", m)}
      onCancelar={() => setModalCancelar(false)}
    />
  );
  const cajaErrorAccion = errorAccion && (
    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
      {errorAccion.texto}
      {errorAccion.link && <> · <Link to={errorAccion.link.ruta} className="font-semibold underline">{errorAccion.link.label}</Link></>}
    </p>
  );

  // Un borrador no tiene nada en vivo: se retoma en el asistente.
  if (c.estado === "borrador") {
    return (
      <div className="space-y-4">
        <Link to={`${B}/campanas`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft size={13} /> Campañas</Link>
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">{c.nombre}</h2>
            <ChipEstadoCampanaOficial estado={c.estado} />
          </div>
          <RenglonSeguimiento campana={c} />
          <RenglonRepeticion campana={c} />
          <p className="text-sm text-slate-600">{equipo ? "Esta campaña todavía no se confirmó." : "Esta campaña todavía no se confirmó: la está armando el equipo de Deenex."}</p>
          {equipo && (
            <div className="flex gap-2 flex-wrap items-start">
              <Link to={rutaEditar} className="h-9 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 inline-flex items-center">Seguir configurando</Link>
              {/* un seguimiento no se duplica: su copia saldría a toda la audiencia de la original */}
              {!c.seguimientoDe && <BotonDuplicarCampana id={c.id} />}
              {botonCancelar}
              <BorrarBorrador id={c.id} nombre={c.nombre}
                onBorrada={() => navigate(`${B}/campanas`, { state: { aviso: `Se borró el borrador «${c.nombre}».` } })} />
            </div>
          )}
          {cajaErrorAccion}
        </div>
        {modal}
      </div>
    );
  }

  const e = c.embudo;
  const k = c.contadores;
  // `total` es lo asignado al envío: ya viene sin los `control` de campañas viejas (contadoresDesdeConteos).
  const aEnviar = Math.max(0, k?.total || 0);
  const confirmo = [...(c.auditoria || [])].reverse().find((a) => a.accion === "confirmar");
  // Quién confirmó, dicho como persona: decía «por alan vía hatsu-demo@operador.deenex» (el email del token). A una
  // persona de un cliente, alguien del equipo es «Equipo Deenex», sin su mail.
  const quienConfirmo = nombreDeQuienPara(confirmo?.por?.email, equipo);
  const motivoCorte = motivoCorteVisible(c);
  const corte = textoCorte(motivoCorte, c);
  const codigoCorte = motivoCorte?.codigo as CodigoCorte | undefined;
  // Confirmada en otro modo (C1): con la cuenta de hoy no sale nunca. Reanudar no se ofrece aunque
  // el flag venga prendido, y el aviso (ámbar: no es un error de la marca) se ve en cualquier estado abierto.
  // Manda `otroModo` del backend: un corte `modo_distinto` de cuando el servidor estaba en el otro modo
  // ya se puede reanudar si volvió. Sólo sin el campo (un backend anterior) se decide por el código.
  const otroModo = c.otroModo ?? codigoCorte === "modo_distinto";
  const puedeReanudar = c.puedeReanudar && !otroModo;
  // Va en los dos sentidos: la de prueba con el servidor en twilio (se cancela y se arma otra) o una
  // real con el servidor de vuelta en simulado (cancelarla la pierde: el aviso no lo ofrece).
  const avisoOtroModo = textoModoDistinto(c.simulado);
  const realEnSimulado = c.simulado === false;
  const alertas = (c.alertas || []).filter((a) => (a.nivel === "alerta" || a.nivel === "corte") && !(c.estado === "pausada_auto" && a.nivel === "corte"));
  const hayBajas = (c.ultimasRespuestas || []).some((r) => r.esBaja) || (respuestas || []).some((r) => r.esBaja);
  const destinosSugeridos = (() => { try { const n = localStorage.getItem(NUMERO_PRUEBA_KEY); return n ? [n] : []; } catch { return []; } })();
  const grupos = c.audiencia?.grupos?.length ? c.audiencia.grupos.map((g) => nombresGrupo[g] || g).join(", ") : "todos los grupos";
  // Sin análisis de Activa detrás (un público de la base o el Público de Activa): sin tabla por grupo, y la recompra se
  // mide con la base de ventas en uso (sin archivos de seguimiento que subir).
  const esBase = c.audiencia?.tipo === "base" || c.audiencia?.tipo === "publico";
  // El backend calcula el desvío contra lo esperado para la campaña COMPLETA: con el envío a medias
  // (o cortado por una cancelación) compara parcial contra total y da «−97 %» sin decir nada. Sólo
  // se muestra con la campaña terminada; antes, lo real va rotulado «hasta ahora».
  const costoCerrado = c.estado === "finalizada" || c.estado === "cancelada";
  // El desvío (lo real contra lo estimado, en %) es jerga del equipo: una persona de un cliente (Accesos, rol «comercio»)
  // ve lo que costó y lo que se estimaba, sin el «desvío −3,7 %» ni el «+3,7 % vs lo estimado».
  const conDesvio = c.estado === "finalizada" && equipo;
  const textoAudiencia = !c.audiencia
    ? "sin audiencia"
    : c.audiencia.tipo === "publico"
      // «Público de Activa · En riesgo»: el nombre ya dice de dónde y qué se eligió
      ? c.audiencia.nombre || "Público de Activa"
    : esBase
      ? `Público «${c.audiencia.nombre}»${c.audiencia.publicoTipo ? ` (${c.audiencia.publicoTipo === "estatico" ? "Fijo" : "Dinámico"})` : ""}`
      : `${c.audiencia.nombre} · ${grupos}`;

  return (
    <div className="space-y-5">
      {/* Encabezado */}
      <div className="space-y-2">
        <Link to={`${B}/campanas`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft size={13} /> Campañas</Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">{c.nombre}</h2>
              <ChipEstadoCampanaOficial estado={c.estado} />
              {c.simulado && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">simulada</span>}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {c.plantilla ? <Link to={`${B}/plantillas/${encodeURIComponent(c.plantilla.id)}`} className="text-violet-600 hover:underline">{c.plantilla.nombreVisible}</Link> : "Sin plantilla"}
              {" · "}{textoAudiencia}
            </p>
            <RenglonSeguimiento campana={c} />
            <RenglonRepeticion campana={c} />
            <p className="text-xs text-slate-400 mt-1">
              {c.estado === "programada" && c.programadaPara && <span className="text-indigo-600 font-medium">Sale el {fechaHora(c.programadaPara)} · </span>}
              {c.confirmadaAt && <>Confirmada {fechaHora(c.confirmadaAt)}{quienConfirmo ? ` por ${quienConfirmo}` : ""}</>}
              {c.iniciadaAt && <> · empezó {fechaHora(c.iniciadaAt)}</>}
              {c.finalizadaAt && <> · terminó {fechaHora(c.finalizadaAt)}</>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-start">
            {/* El equipo y el cliente, desde que salió algo: qué trae el archivo para cada uno lo decide el backend. */}
            {empezo && <BajarResultadosCampana campanaId={c.id} />}
            {equipo && <>
            {c.puedePausar && (
              <button type="button" onClick={() => void accion("pausar")} disabled={!!busy} title="Lo que ya salió no se toca; lo que falta queda en espera."
                className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy === "pausar" ? <Loader2 size={13} className="animate-spin" /> : <Pause size={13} />} Pausar
              </button>
            )}
            {puedeReanudar && (
              <button type="button" onClick={() => void accion("reanudar")} disabled={!!busy}
                className="h-9 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy === "reanudar" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Reanudar
              </button>
            )}
            {c.puedeEditar && <Link to={rutaEditar} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center">Seguir configurando</Link>}
            {/* A una parte de los que la recibieron (28/10/2026): sólo si ya mandó y no tiene el suyo (lo decide el backend) */}
            {c.puedeHacerSeguimiento && (
              <Link to={rutaHacerSeguimiento(c.id)} title="Una campaña nueva sólo para los que no la leyeron, no tocaron el link o no compraron."
                className="h-9 px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5">
                <CornerDownRight size={13} /> Hacer un seguimiento
              </Link>
            )}
            {/* Cada semana, cada 2 o cada mes (28/10/2026): una confirmada que no es seguimiento ni repetición, y todavía no se repite */}
            {c.puedeRepetir && (!c.repeticion || c.repeticion.estado === "terminada") && (
              <Link to={rutaRepetir(c.id)} title="Que se repita cada semana, cada 2 semanas o cada mes, hasta una fecha o unas veces."
                className="h-9 px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5">
                <Repeat size={13} /> Repetir esta campaña
              </Link>
            )}
            {/* Desde cualquier estado: un borrador nuevo con la misma configuración, sin nada de lo que salió (un seguimiento, no) */}
            {!c.seguimientoDe && <BotonDuplicarCampana id={c.id} />}
            {botonCancelar}
            </>}
          </div>
        </div>
        {equipo && c.puedePausar && <p className="text-[11px] text-slate-400">Pausar: lo que ya salió no se toca; lo que falta queda en espera.</p>}
      </div>

      {/* Banners */}
      <div className="space-y-2">
        {avisoVigente(aviso, c.estado) && <Banner tono="verde" icono={<CheckCircle2 size={16} />}>{aviso}</Banner>}
        {cajaErrorAccion}
        {pollCampana.detenido && (
          <Banner tono="ambar" icono={<AlertTriangle size={16} />}>
            <p>Dejamos de actualizar porque el servidor no responde. Lo que ves es lo último que trajimos.</p>
            <button type="button" onClick={pollCampana.reintentar} className="font-semibold underline">Reintentar</button>
          </Banner>
        )}
        {otroModo && ABIERTA.includes(c.estado) && (
          <Banner tono="ambar" icono={<AlertTriangle size={16} />}>
            <p className="font-semibold">{avisoOtroModo.titulo}{motivoCorte?.at && <span className="font-normal text-amber-700/80"> · {fechaHora(motivoCorte.at)}</span>}</p>
            <p>{avisoOtroModo.detalle}</p>
            {equipo && c.puedeCancelar && !realEnSimulado && <button type="button" onClick={() => setModalCancelar(true)} disabled={!!busy} className="font-semibold underline">Cancelar la campaña</button>}
          </Banner>
        )}
        {!otroModo && c.estado === "pausada_auto" && corte && (
          <Banner tono="rojo" icono={<OctagonAlert size={16} />}>
            <p className="font-semibold">{corte.titulo}{motivoCorte?.at && <span className="font-normal text-red-700/80"> · {fechaHora(motivoCorte.at)}</span>}</p>
            {corte.detalle && <p>{corte.detalle}</p>}
            <div className="flex gap-3 flex-wrap">
              {codigoCorte?.startsWith("plantilla_") && c.plantilla && (
                <Link to={`${B}/plantillas/${encodeURIComponent(c.plantilla.id)}`} className="font-semibold underline">Ver la plantilla</Link>
              )}
              {equipo && (codigoCorte === "marca_frenada" || codigoCorte === "cuenta_no_lista" || codigoCorte === "twilio_sin_respuesta" || codigoCorte === "sender_offline" || codigoCorte === "sin_callbacks") && (
                <Link to={RUTA_CUENTA} className="font-semibold underline">Ir a Configuración</Link>
              )}
              {(codigoCorte === "saldo" || codigoCorte === "tope_mensual") && (
                <Link to={RUTA_SALDO} className="font-semibold underline">{!equipo ? "Ver saldo" : codigoCorte === "saldo" ? "Cargar saldo" : "Ver tope"}</Link>
              )}
              {equipo && puedeReanudar && <button type="button" onClick={() => void accion("reanudar")} disabled={!!busy} className="font-semibold underline">Reanudar</button>}
            </div>
            {/* Con la marca frenada el backend no ofrece Reanudar (daba 422 MARCA_FRENADA): se dice qué va primero. El
                corte por la marca ya lo dice en su texto. */}
            {equipo && !c.puedeReanudar && cuenta?.frenada?.activa && codigoCorte !== "marca_frenada" && (
              <p>Para reanudarla, primero hay que reanudar la marca en <Link to={RUTA_CUENTA} className="font-semibold underline">Configuración</Link>.</p>
            )}
          </Banner>
        )}
        {c.estado === "pausada" && codigoCorte === "programacion_vencida" && (
          <Banner tono="ambar" icono={<Clock size={16} />}>
            <p className="font-semibold">{CORTE.programacion_vencida.titulo}</p>
            <p>{CORTE.programacion_vencida.detalle}</p>
          </Banner>
        )}
        {c.estado === "enviando" && c.espera && (
          <BannerEspera espera={c.espera} campana={c} />
        )}
        {alertas.map((a, i) => (
          <Banner key={`${a.codigo}-${i}`} tono={a.nivel === "corte" ? "rojo" : "ambar"} icono={<AlertTriangle size={16} />}>
            <p>{a.mensaje}</p>
          </Banner>
        ))}
      </div>

      {/* la regla de la repetición, la próxima y sus repeticiones (28/10/2026): la ven todos; los botones, el equipo */}
      {c.repeticion && <TarjetaRepeticion campana={c} equipo={equipo} onActualizada={setCampana} />}

      <Tabs tabs={equipo ? TABS : TABS_CLIENTE} value={tab} onChange={setTab} alerta={{ fallidos: (e?.fallidos || 0) > 0, respuestas: hayBajas }} />

      <div id={`panel-oficial-${tab}`} role="tabpanel" aria-labelledby={`tab-oficial-${tab}`} className="space-y-5">
        {tab === "vivo" && (
          <>
            <Tarjeta>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <p className="text-sm text-slate-700"><strong className="text-2xl text-slate-900">{num(k?.enviados || 0)}</strong> de {num(aEnviar)} {plural(k?.enviados || 0, "salió", "salieron")}</p>
                <span className="text-sm font-semibold text-violet-700">{pct(k?.enviados || 0, aEnviar)} %</span>
              </div>
              <div className="mt-2 h-2.5 rounded-full bg-violet-100 overflow-hidden">
                <div className="h-full rounded-full bg-violet-600 transition-all duration-700" style={{ width: `${Math.min(100, pct(k?.enviados || 0, aEnviar))}%` }} />
              </div>
            </Tarjeta>

            {e && (
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                <StatTile icon={<Clock size={15} />} label="En cola" value={num(e.enCola)} tono="neutro" />
                <StatTile icon={<Send size={15} />} label="Enviados" value={num(e.enviados)} tono="entrega" sub={`de ${num(aEnviar)}`} />
                <StatTile icon={<CheckCheck size={15} />} label="Entregados" value={num(e.entregados)} tono="entrega" sub={`${pct(e.entregados, e.enviados)} % de enviados`} meterPct={pct(e.entregados, e.enviados)} />
                <StatTile icon={<CheckCheck size={15} />} label="Leídos" value={num(e.leidos)} tono="lectura" sub="sólo con confirmación de lectura" />
                <StatTile icon={<MessageSquare size={15} />} label="Respondieron" value={num(e.respondieron)} tono="respuesta" />
                <StatTile icon={<UserMinus size={15} />} label="Bajas" value={num(e.bajas)} tono="baja" sub={`${pct(e.bajas, e.enviados)} % de los enviados`} />
                <StatTile icon={<XCircle size={15} />} label="Fallidos" value={num(e.fallidos)} tono="baja" sub="ver motivos" />
              </div>
            )}

            {e && <FilaSecundaria embudo={e} />}

            <EmbudoOficial embudo={e || null} simulado={c.simulado} medicion={resultado?.medicion} leidoAclaracion={resultado?.leidoAclaracion} />

            {empezo && stats && stats.serieHoras?.some((h) => (Number(h.enviados) || 0) > 0) && (
              <Tarjeta titulo="Por hora">
                <GraficoPorHora serie={stats.serieHoras} />
              </Tarjeta>
            )}

            {c.chequeo?.plan && (
              <Tarjeta titulo="Plan de envío">
                {c.horario && <p className="mb-3 text-sm text-slate-700">{textoHorario(c.horario, c.franjasTexto)}</p>}
                <PlanTandas plan={c.chequeo.plan} cupo24h={c.chequeo.cupo24h} leyenda="Plan calculado al confirmar; el ritmo real depende del cupo de Meta y la franja." />
              </Tarjeta>
            )}

            <Tarjeta titulo="Costo">
              <p className="text-sm text-slate-700">
                Estimado: hasta <strong>{usd(c.costoEstimado?.clienteMaximoUSD)}</strong> (esperado {usd(c.costoEstimado?.clienteEsperadoUSD)})
              </p>
              {c.simulado ? (
                // El costo real excluye lo simulado (§5.4): mostrar «USD 0 · −100 % vs lo estimado» haría creer
                // que falló el cobro, cuando lo que salió sí se descontó del saldo simulado.
                <p className="text-sm text-slate-500 mt-1">Campaña simulada: lo que salió se descontó del saldo simulado, pero no es un cobro real.</p>
              ) : resultado ? (
                <p className="text-sm text-slate-700 mt-1">
                  {costoCerrado ? "Real" : "Real hasta ahora"}: <strong>{usd(resultado.costoReal?.clienteUSD)}</strong>
                  {conDesvio && typeof resultado.costoReal?.desvioPct === "number" && Number.isFinite(resultado.costoReal.desvioPct) && (
                    <span className="text-slate-500"> ({resultado.costoReal.desvioPct > 0 ? "+" : ""}{resultado.costoReal.desvioPct.toLocaleString("es-AR", { maximumFractionDigits: 1 })} % vs lo estimado)</span>
                  )}
                </p>
              ) : (
                <p className="text-sm text-slate-500 mt-1">{errorResultado || (empezo ? "Trayendo el costo real…" : "Todavía no salió ningún mensaje.")}</p>
              )}
              {pollResultado.detenido && <p className="text-xs text-amber-700 mt-1">Dejamos de actualizar los resultados. <button type="button" onClick={pollResultado.reintentar} className="underline">Reintentar</button></p>}
            </Tarjeta>

            <Tarjeta titulo="Últimas respuestas" right={<button type="button" onClick={() => setTab("respuestas")} className="text-xs font-semibold text-violet-600 hover:underline">Ver todas</button>}>
              <ListaRespuestas items={(c.ultimasRespuestas || []).slice(0, 5)} />
            </Tarjeta>
          </>
        )}

        {tab === "fallidos" && (
          <Tarjeta titulo="Fallidos por motivo" sub="Por qué no llegaron, según WhatsApp">
            <PanelResultado resultado={resultado} error={errorResultado} cargando={cargandoResultado} empezo={empezo}>
              {(r) => <FallidosPorMotivo filas={r.fallidosPorCodigo} onVerMensajes={verMensajesDe} />}
            </PanelResultado>
          </Tarjeta>
        )}

        {tab === "mensajes" && (
          <Tarjeta titulo="Mensajes" sub="Un renglón por persona">
            <BitacoraMensajes key={claveBitacora} campanaId={c.id} filtroInicial={filtroMensajes} codigos={resultado?.fallidosPorCodigo} nombresGrupo={nombresGrupo} />
          </Tarjeta>
        )}

        {tab === "respuestas" && (
          <>
            <Tarjeta titulo="Respuestas" right={
              <button type="button" onClick={() => void cargarRespuestas()} disabled={cargandoRespuestas}
                className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                {cargandoRespuestas ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Actualizar
              </button>
            }>
              {errorRespuestas ? (
                <p className="text-sm text-red-700" role="alert">No pudimos traer las respuestas. {errorRespuestas}</p>
              ) : respuestas === null ? (
                <div className="h-24 rounded-xl bg-slate-50 animate-pulse" />
              ) : (
                <ListaRespuestas items={respuestas} />
              )}
            </Tarjeta>
            {modo === "simulated" && equipo && (
              <SimularRespuesta destinosSugeridos={destinosSugeridos} onHecho={() => { void cargarRespuestas(); void cargarCampana(); }} />
            )}
          </>
        )}

        {tab === "resultado" && (
          <PanelResultado resultado={resultado} error={errorResultado} cargando={cargandoResultado} empezo={empezo}>
            {(r) => (
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* cada tasa dice su base (entrega y bajas sobre los enviados; lectura y respuesta sobre los entregados) */}
                  <StatTile icon={<CheckCheck size={15} />} label="Entrega" value={tasaTexto(r.tasas.entrega)} tono="entrega" meterPct={r.tasas.entrega * 100}
                    sub={`de los ${BASE_TASA.entrega}`} chip={r.hayDatosSimulados ? { texto: "simulado", cls: "bg-amber-100 text-amber-700" } : undefined} />
                  <StatTile icon={<CheckCheck size={15} />} label="Lectura" value={tasaTexto(r.tasas.lectura)} tono="lectura" meterPct={r.tasas.lectura * 100}
                    sub={`de los ${BASE_TASA.lectura}${r.leidoAclaracion ? `. ${r.leidoAclaracion}` : ""}`} />
                  <StatTile icon={<MessageSquare size={15} />} label="Respuesta" value={tasaTexto(r.tasas.respuesta)} tono="respuesta" meterPct={r.tasas.respuesta * 100} sub={`de los ${BASE_TASA.respuesta}`} />
                  <StatTile icon={<UserMinus size={15} />} label="Bajas" value={tasaTexto(r.tasas.bajas)} tono="baja" meterPct={r.tasas.bajas * 100} sub={`de los ${BASE_TASA.bajas}`} />
                </div>
                {stats ? (
                  <>
                    {/* Un público de la base no tiene grupos de Activa: la tabla sería una sola fila «Sin grupo» que repite los tiles. */}
                    {!esBase && (
                      <Tarjeta titulo="Por grupo">
                        <TablaPorGrupo filas={stats.porGrupo} nombres={nombresGrupo} />
                      </Tarjeta>
                    )}
                    <Tarjeta titulo="Botones y bajas">
                      <TarjetaRespuestas compacta botones={stats.botones} bajasPorVia={stats.bajasPorVia} optOut={stats.optOut} respuestasCitando={stats.respuestasCitando} />
                    </Tarjeta>
                  </>
                ) : errorStats ? (
                  <ErrorTarjeta texto={`No pudimos traer ${esBase ? "los tiempos ni los botones" : "los tiempos, los grupos ni los botones"}. ${errorStats}`} onReintentar={() => void cargarStats()} />
                ) : (
                  <div className="h-32 rounded-xl bg-slate-50 animate-pulse" aria-busy="true" />
                )}
                <Tarjeta titulo="Costo real">
                  {/* Lo que pagó Deenex (`realUSD`) es del equipo: a una persona de un cliente, sólo lo que se le cobra a ella. */}
                  <p className="text-sm text-slate-700">
                    Costo real{costoCerrado ? "" : " hasta ahora"} {usd(r.costoReal?.clienteUSD)}{equipo && <> (Deenex pagó {usd(r.costoReal?.realUSD)})</>} · estimado {usd(r.costoReal?.estimadoClienteUSD)}
                    {conDesvio ? <> · desvío {(Number(r.costoReal?.desvioPct) || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })} %</> : " para la campaña completa"}
                  </p>
                </Tarjeta>
                {stats?.clics?.activo && (
                  <Tarjeta titulo="Clics en los links">
                    <TarjetaClics clics={stats.clics} />
                  </Tarjeta>
                )}
                <Tarjeta titulo="Fallidos por motivo">
                  <FallidosPorMotivo filas={r.fallidosPorCodigo} onVerMensajes={verMensajesDe} />
                </Tarjeta>
                {esBase ? (
                  <Tarjeta titulo={TITULO_MEDICION_BASE} sub={SUBTITULO_MEDICION_BASE}>
                    <MedicionBaseVentas medicion={r.medicion} simulado={c.simulado} />
                  </Tarjeta>
                ) : (
                  <Tarjeta titulo={TITULO_MEDICION} sub={SUBTITULO_MEDICION}>
                    <MedicionRecompra campana={c} resultado={r} onMedido={(m: Medicion) => setResultado((prev) => (prev ? { ...prev, medicion: m } : prev))} />
                  </Tarjeta>
                )}
                {/* Lo técnico, plegado como en Estadísticas: los tiempos por tramo y el precio que informa Twilio
                    son del equipo de Deenex. Estaban al mismo nivel que los resultados y el comercio leía «Twilio»
                    en la pantalla de su campaña. El precio de Twilio es lo que le cuesta a Deenex: a una persona
                    de un cliente no se le muestra (ve los tiempos de sus envíos, como en Estadísticas). */}
                {(equipo || stats) && (
                  <DetalleTecnico subtitulo={equipo ? "Tiempos de entrega y el precio que informa Twilio" : "Tiempos de entrega de tus envíos"}>
                    {stats && (
                      <Tarjeta titulo="Tiempos de entrega" sub="Mediana y en cuánto llegan 9 de cada 10">
                        <TarjetaTiempos tiempos={stats.tiempos} />
                      </Tarjeta>
                    )}
                    {equipo && (
                      <Tarjeta titulo="Costo según Twilio">
                        {stats?.costoTwilio ? <TarjetaCostoTwilio costo={stats.costoTwilio} />
                          : <p className="text-sm text-slate-500">{errorStats ? "No pudimos traer el precio que informa Twilio." : "Trayendo el precio que informa Twilio…"}</p>}
                      </Tarjeta>
                    )}
                  </DetalleTecnico>
                )}
              </div>
            )}
          </PanelResultado>
        )}

        {tab === "auditoria" && equipo && (
          <Tarjeta titulo="Auditoría" sub="Quién hizo qué y cuándo">
            {!c.auditoria?.length ? (
              <p className="text-sm text-slate-400">Sin registros.</p>
            ) : (
              <ol className="divide-y divide-slate-100">
                {[...c.auditoria].reverse().map((a, i) => (
                  <li key={`${a.at}-${i}`} className="flex items-start justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800">{textoAccion(a)}</p>
                      <p className="text-xs text-slate-500">{nombreDeQuienPara(a.por?.email, equipo) || "—"}{a.por?.ip && <span className="ml-2 text-[10px] text-slate-400">{a.por.ip}</span>}</p>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{fechaHora(a.at)}</span>
                  </li>
                ))}
              </ol>
            )}
          </Tarjeta>
        )}
      </div>
      {modal}
    </div>
  );
}

function BannerEspera({ espera, campana }: { espera: Exclude<EsperaCampana, null>; campana: CampanaDetalle }) {
  const info = ESPERA[espera];
  // El límite de Meta, no el que dejó el freno: «Meta deja escribirle a 1.000» con un límite de 2.000 era falso.
  const cupo = campana.chequeo?.cupo24h;
  const detalle = detalleEspera(espera, {
    franjasTexto: campana.franjasTexto,
    esperaHasta: campana.esperaHasta,
    limite: cupo?.limiteMeta ?? cupo?.limite ?? null,
    sinSalirAlFin: campana.sinSalirAlFin,
    hasta: campana.horario?.hasta,
  });
  return (
    <Banner tono="ambar" icono={<Clock size={16} />}>
      <p className="font-semibold">{info?.titulo || espera}</p>
      <p>{detalle}</p>
      {info?.accion && <Link to={info.accion.ruta} className="font-semibold underline">{info.accion.label}</Link>}
    </Banner>
  );
}

/** Lo que no se ve en los tiles: esperas, reintentos, confirmaciones pendientes y excluidos. Sólo lo que es > 0. */
function FilaSecundaria({ embudo }: { embudo: NonNullable<CampanaDetalle["embudo"]> }) {
  const partes: string[] = [];
  // lo que el plan dice que sale mañana (el motivo de la espera lo dice el aviso de arriba)
  if (embudo.programadosParaManana > 0) partes.push(`${num(embudo.programadosParaManana)} ${plural(embudo.programadosParaManana, "sale", "salen")} mañana`);
  if (embudo.reintentosProgramados > 0) partes.push(`${num(embudo.reintentosProgramados)} con reintento en 24 h`);
  if (embudo.desconocidos > 0) partes.push(`${num(embudo.desconocidos)} esperando confirmación`);
  if (embudo.sinConfirmacion > 0) partes.push(`${num(embudo.sinConfirmacion)} sin confirmación (no se cuentan como entregados)`);
  const ex = embudo.excluidosAlDespachar || {};
  for (const m of ORDEN_GATES) {
    const n = Number(ex[m]) || 0;
    if (n > 0) partes.push(`${num(n)} no se ${plural(n, "mandó", "mandaron")}: ${MOTIVO_EXCLUSION[m].titulo}`);
  }
  if (!partes.length) return null;
  return <p className="text-xs text-slate-500 leading-relaxed">{partes.join(" · ")}</p>;
}

/** Envoltorio de lo que depende del resultado: cargando, error o «todavía no salió nada», nunca ceros. */
function PanelResultado({ resultado, error, cargando, empezo, children }: {
  resultado: Resultado | null; error: string; cargando: boolean; empezo: boolean; children: (r: Resultado) => ReactNode;
}) {
  if (resultado) return <>{children(resultado)}</>;
  if (!empezo) return <p className="text-sm text-slate-500">Todavía no salió ningún mensaje.</p>;
  if (error) return <p className="text-sm text-red-700" role="alert">{error}</p>;
  if (cargando) return <div className="h-32 rounded-xl bg-slate-50 animate-pulse" />;
  return <p className="text-sm text-slate-500">No pudimos traer los resultados.</p>;
}
